import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Ticket, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { acceptSession } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/users/invite/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This invitation link is invalid or expired."));
  }, [token]);

  const submit = async () => {
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/users/accept-invite`, { token, password });
      acceptSession(r.data.token, r.data.user);
      toast.success(`Welcome, ${r.data.user.name}!`);
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept invite");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grain min-h-screen w-full flex items-center justify-center p-6" style={{ background: "#FFFFFF" }}>
      <div className="hairline rounded-lg bg-white p-6 max-w-md w-full">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-md bg-[#4f4c90] flex items-center justify-center">
            <Ticket size={20} weight="duotone" color="white" />
          </div>
          <div>
            <div className="font-heading font-bold">Aurora Business Systems Support</div>
            <div className="text-[11px] text-slate-500 font-mono">Accept invitation</div>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-red-400 bg-red-500/10 border border-red-500/25 p-3 rounded">
            <WarningCircle size={16} weight="fill" className="mt-0.5" />
            <div className="text-sm">{error}</div>
          </div>
        )}

        {!error && !info && <div className="text-slate-500 text-sm">Loading invitation…</div>}

        {info && (
          <>
            <div className="text-sm text-slate-700 mb-4">
              You've been invited as <span className="font-mono text-[#8B87C8] capitalize">{info.role}</span>.
              Set a password to activate your account.
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-700">Name</Label>
                <Input value={info.name} disabled className="mt-1 bg-[#FFFFFF]/50 border-slate-200 opacity-70" />
              </div>
              <div>
                <Label className="text-xs text-slate-700">Email</Label>
                <Input value={info.email} disabled className="mt-1 bg-[#FFFFFF]/50 border-slate-200 opacity-70 font-mono text-xs" />
              </div>
              <div>
                <Label className="text-xs text-slate-700">New password (min 8 chars)</Label>
                <Input
                  data-testid="accept-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 bg-[#FFFFFF] border-slate-200"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-700">Confirm password</Label>
                <Input
                  data-testid="accept-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                  className="mt-1 bg-[#FFFFFF] border-slate-200"
                  placeholder="••••••••"
                />
              </div>
              <Button
                data-testid="accept-submit"
                onClick={submit}
                disabled={saving || !password || !confirm}
                className="w-full bg-[#4f4c90] hover:bg-[#5F5CA5] mt-2 gap-1"
              >
                <CheckCircle size={14} weight="fill" /> {saving ? "Setting up…" : "Activate account"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
